import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className="flex items-center gap-2 text-foreground-900 hover:text-primary-600 transition-colors"
          >
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center">
              <i className="ri-file-list-3-line text-xl md:text-2xl text-primary-500"></i>
            </div>
            <span className="font-heading text-lg md:text-xl font-semibold whitespace-nowrap">
              CSSBuy Sheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1" ref={dropdownRef}>
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors rounded-lg hover:bg-background-100 whitespace-nowrap cursor-pointer"
              >
                <span>Categories</span>
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </div>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-1 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-3 w-[480px] z-50">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 px-3">
                    {categories.map((cat, i) => (
                      <a
                        key={i}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 px-3 py-2 rounded-lg transition-colors whitespace-nowrap"
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors whitespace-nowrap"
            >
              cssbuy sheet
            </a>
          </div>

          <div className="hidden md:block">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={mobileMenuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
            </div>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 shadow-lg">
          <div className="px-4 py-4 space-y-1 max-h-[70vh] overflow-y-auto">
            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-3 mb-2">Categories</p>
            {categories.map((cat, i) => (
              <a
                key={i}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-sm text-foreground-700 hover:text-primary-600 hover:bg-primary-50 px-3 py-2.5 rounded-lg transition-colors"
              >
                {cat.name}
              </a>
            ))}
            <div className="pt-3 border-t border-background-200/70 space-y-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-sm font-medium text-primary-600 hover:bg-primary-50 px-3 py-2.5 rounded-lg transition-colors"
              >
                cssbuy sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-center text-sm font-semibold bg-primary-500 text-background-50 px-3 py-2.5 rounded-full hover:bg-primary-600 transition-all"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}